// ===== Shared drawing helpers (canvas) =====
// The basic shapes (rr, lg, shadow, block, colours) are used by graphics.ts and the kit and also exist as GFX.rr etc.
// The rest (outline, face, drawBall, bubble, basket, selBox) are collected from copies in the worlds, so they can share one version.
import type { Ctx2D, GameBody, Vec } from './types';

/** The game's fixed colours */
export const C = {
  ink: '#3B2A5C', inkSoft: 'rgba(59,42,92,.35)', shadow: 'rgba(70,35,90,.28)',
  wall1: '#FFEFD6', wall2: '#FFDDB5', tile: 'rgba(255,255,255,.55)',
  wood1: '#F7B267', wood2: '#D9803E', wood3: '#B8612A',
  mint1: '#7FE0CF', mint2: '#3FB5A3',
  lilac1: '#C9B6F2', lilac2: '#9A83DB',
  white1: '#FFFFFF', white2: '#DCE8F5',
  red1: '#FF7A7A', red2: '#E0394A', pea1: '#9BE15D', pea2: '#3FA535',
  berry1: '#8C9BFF', berry2: '#4A4FCB', gold1: '#FFE07A', gold2: '#F2A516',
  paint: '#FF5DB1', stone1: '#C9C3D6', stone2: '#8B84A0',
};
export const DEG = Math.PI / 180;
export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** Rounded rectangle (path, x/y = top left corner) */
export function rr(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number) {
  r = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
/** Vertical gradient */
export function lg(ctx: Ctx2D, y0: number, y1: number, c0: string, c1: string) { const g = ctx.createLinearGradient(0, y0, 0, y1); g.addColorStop(0, c0); g.addColorStop(1, c1); return g; }
export function shadowOn(ctx: Ctx2D, blur?: number | null, oy?: number | null) { ctx.shadowColor = C.shadow; ctx.shadowBlur = blur == null ? 14 : blur; ctx.shadowOffsetY = oy == null ? 7 : oy; }
export function shadowOff(ctx: Ctx2D) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0; }

/** "Soft block" – rounded box with top light (x/y = centre, a = angle in radians) */
export function block(ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number | null | undefined, c1: string, c2: string, rad?: number | null, opts?: { flat?: boolean; noShadow?: boolean; outline?: string | null }) {
  opts = opts || {};
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  const R0 = rad == null ? Math.min(10, h / 2) : rad, dep = opts.flat ? 0 : Math.min(7, Math.max(3, h * 0.35));
  if (!opts.noShadow) shadowOn(ctx);
  // 3D side (slightly darker, offset downwards)
  if (dep) { rr(ctx, -w / 2, -h / 2 + dep, w, h, R0); ctx.fillStyle = c2; ctx.fill(); shadowOff(ctx); ctx.fillStyle = 'rgba(40,20,60,.22)'; ctx.fill(); }
  rr(ctx, -w / 2, -h / 2, w, h, R0);
  ctx.fillStyle = lg(ctx, -h / 2, h / 2, c1, c2); ctx.fill();
  shadowOff(ctx);
  // top gloss
  rr(ctx, -w / 2 + 4, -h / 2 + 2, w - 8, Math.max(3, h * 0.28), Math.min(6, h / 4));
  ctx.fillStyle = 'rgba(255,255,255,.45)'; ctx.fill();
  // clear dark outline, so the block stands out from the background
  const Rr = rad == null ? Math.min(10, h / 2) : rad;
  rr(ctx, -w / 2, -h / 2, w, h + dep, Rr); ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(59,42,92,.85)'; ctx.stroke();
  if (opts.outline) { rr(ctx, -w / 2 - 4, -h / 2 - 4, w + 8, h + dep + 8, Rr + 4); ctx.lineWidth = 5; ctx.strokeStyle = '#FFC93C'; ctx.stroke(); }
  ctx.restore();
}

// ---------- Helpers collected from the worlds ----------
/** Dark outline around the current path */
export function outline(ctx: Ctx2D, w?: number, color = 'rgba(59,42,92,.9)') { ctx.lineWidth = w || 3; ctx.strokeStyle = color; ctx.stroke(); }

export interface FaceOpts {
  /** How much the eyes follow the speed (default 0.5) */
  look?: number;
  /** Small pupils and open mouth at high speed/falling (default true) */
  scared?: boolean;
  ink?: string;
  cheek?: string;
}
/** The ball's face, drawn around (0, 0) with radius r. v = speed (the eyes look that way). */
export function face(ctx: Ctx2D, r: number, v: Vec, sad?: boolean, opts: FaceOpts = {}) {
  const ink = opts.ink || C.ink, look = opts.look ?? 0.5, react = opts.scared !== false;
  const lx = clamp(v.x * look, -2.5, 2.5), ly = clamp(v.y * look, -2.5, 2.5);
  const sp = Math.hypot(v.x, v.y), scared = react && v.y > 9, fast = react && sp > 8.5;
  const ex = r * 0.34, ey = -r * 0.12, er = r * 0.27;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.beginPath(); ctx.arc(s * ex + lx * 0.6, ey + ly * 0.6, er * (scared ? 0.35 : 0.55), 0, 7); ctx.fillStyle = ink; ctx.fill();
  }
  ctx.fillStyle = ink; ctx.strokeStyle = ink; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (sad) { ctx.beginPath(); ctx.arc(0, r * 0.62, r * 0.26, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast || scared) { ctx.beginPath(); ctx.ellipse(0, r * 0.42, r * 0.17, r * 0.22, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.25, r * 0.28, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = opts.cheek || 'rgba(255,120,150,.5)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.58, r * 0.22, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); });
}

export interface BallOpts {
  /** Gradient (light, mid, dark) for hero and helper */
  hero?: [string, string, string];
  helper?: [string, string, string];
  /** Outline (width, colour); omitted: none */
  outline?: [number, string];
  shadow?: boolean;
  /** Gloss at the top left (alpha) */
  gloss?: number;
  face?: FaceOpts;
}
/** Ball with gradient, face (rolls along) and gloss – the basic shape behind the worlds' drawBall */
export function drawBall(ctx: Ctx2D, b: GameBody, kind: string, opts: BallOpts = {}) {
  const { x, y } = b.position, r = b.circleRadius || 15;
  const c = (kind === 'helper' ? opts.helper : opts.hero) || (kind === 'helper' ? [C.berry1, C.berry1, C.berry2] : [C.pea1, C.pea1, C.pea2]);
  ctx.save(); ctx.translate(x, y);
  if (opts.shadow) shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05);
  gr.addColorStop(0, c[0]); gr.addColorStop(0.5, c[1]); gr.addColorStop(1, c[2]);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill();
  if (opts.shadow) shadowOff(ctx);
  if (opts.outline) outline(ctx, opts.outline[0], opts.outline[1]);
  ctx.save(); ctx.rotate(b.roll || 0); face(ctx, r, b.velocity, b.sad, opts.face); ctx.restore();
  ctx.beginPath(); ctx.ellipse(-r * .38, -r * .45, r * .26, r * .15, -0.6, 0, 7); ctx.fillStyle = `rgba(255,255,255,${opts.gloss ?? 0.7})`; ctx.fill();
  ctx.restore();
}

/** Soap bubble with glints (alpha = transparency for the whole bubble) */
export function bubble(ctx: Ctx2D, x: number, y: number, r: number, opts: { fill?: string; stroke?: string; lineWidth?: number; alpha?: number } = {}) {
  ctx.save(); if (opts.alpha != null) ctx.globalAlpha = opts.alpha;
  ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fillStyle = opts.fill || 'rgba(255,255,255,.35)'; ctx.fill();
  ctx.lineWidth = opts.lineWidth || 2; ctx.strokeStyle = opts.stroke || 'rgba(255,255,255,.95)'; ctx.stroke();
  ctx.beginPath(); ctx.arc(x - r * .35, y - r * .35, r * .22, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  ctx.restore();
}

/** Woven basket (goal shape like the bowl): bottom at y, opening at y - 58, width w. full = colour of the contents. */
export function basket(ctx: Ctx2D, x: number, y: number, w: number, opts: { c1?: string; c2?: string; rim?: string; full?: string | null } = {}) {
  ctx.save();
  shadowOn(ctx, 12, 6);
  ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, y - 58); ctx.lineTo(x - w / 2 + 8, y + 6); ctx.quadraticCurveTo(x, y + 12, x + w / 2 - 8, y + 6); ctx.lineTo(x + w / 2 + 6, y - 58); ctx.closePath();
  ctx.fillStyle = lg(ctx, y - 60, y + 10, opts.c1 || '#F5D08A', opts.c2 || '#C98A3E'); ctx.fill(); shadowOff(ctx); outline(ctx, 3);
  // weave
  ctx.save(); ctx.clip();
  ctx.strokeStyle = 'rgba(140,80,30,.5)'; ctx.lineWidth = 3;
  for (let yy = y - 46; yy < y + 6; yy += 14) { ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, yy); ctx.lineTo(x + w / 2 + 6, yy); ctx.stroke(); }
  for (let xx = x - w / 2; xx < x + w / 2; xx += 18) { ctx.beginPath(); ctx.moveTo(xx, y - 58); ctx.lineTo(xx + 4, y + 10); ctx.stroke(); }
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 9, 0, 0, 7); ctx.fillStyle = opts.rim || '#A8682A'; ctx.fill(); outline(ctx, 2.5);
  if (opts.full) { ctx.beginPath(); ctx.ellipse(x, y - 57, w / 2 - 4, 6, 0, 0, 7); ctx.fillStyle = opts.full; ctx.fill(); }
  ctx.restore();
}

/** Yellow highlight around a selected part (x/y = top left corner) */
export function selBox(ctx: Ctx2D, x: number, y: number, w: number, h: number, r = 10, dashed = false) {
  ctx.save(); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; if (dashed) ctx.setLineDash([8, 6]);
  ctx.beginPath(); rr(ctx, x, y, w, h, r); ctx.stroke(); ctx.restore();
}
